<template>
  <form @submit.prevent="handleSubmit" enctype="multipart/form-data">
    <div class="row">
      <div class="col-md-6 mb-3">
        <label>Nombres</label>
        <input v-model="form.nombres" class="form-control" required />
      </div>

      <div class="col-md-6 mb-3">
        <label>Apellidos</label>
        <input v-model="form.apellidos" class="form-control" required />
      </div>
      
      <div class="col-md-6 mb-3">
        <label>Selecciona tu Rol</label>
        <select v-model="form.rol" class="form-select" required>
          <option value="">Selecciona...</option>
          <option value="odontólogo">odontólogo</option>
          <option value="estudiante">estudiante</option>
          <option value="higienista">higienista</option>
          <option value="otro">otro</option>
        </select>
      </div>

      <div class="col-md-6 mb-3" v-if="form.rol === 'otro'">
        <label>Especifica tu Rol</label>
        <input v-model="form.rol_especificado" class="form-control" placeholder="Ingresa tu rol" />
      </div>

      <div class="col-md-6 mb-3">
        <label>Cédula</label>
        <input v-model="form.cedula" class="form-control" required />
      </div>

      <div class="col-md-6 mb-3">
        <label>Correo electrónico</label>
        <input v-model="form.email" type="email" class="form-control" required />
      </div>

      <div class="col-md-6 mb-3">
        <label>Celular</label>
        <input v-model="form.celular" class="form-control" required />
      </div>

      <!-- Formato (obligatorio, 1 imagen) -->
      <div class="col-md-6 mb-3">
        <label>Cargar la foto del formato de compromiso de actividad gratuita</label>
        <input
          type="file"
          accept="image/png, image/jpeg, image/webp"
          @change="handleFile($event)"
          class="form-control"
          required
        />
        <small class="text-muted">{{ files.formato.length }} imagen seleccionada</small>

        <div class="row mt-2" v-if="previews.formato.length">
          <div class="col-4 col-md-3 col-lg-2 mb-2 position-relative">
            <img
              :src="previews.formato[0]"
              class="img-thumbnail w-100 shadow-sm"
              style="height: 100px; object-fit: cover;"
            />
            <button
              type="button"
              class="btn btn-danger btn-sm rounded-circle d-flex align-items-center justify-content-center position-absolute"
              style="width: 24px; height: 24px; top: 4px; right: 4px; z-index: 2;"
              @click="removeImage(0)"
              title="Eliminar imagen"
            >
              <span style="font-size: 16px; line-height: 1;">&times;</span>
            </button>
          </div>
        </div>
      </div>
    </div>

    <button type="submit" class="btn btn-primary mt-3 w-100" :disabled="isSubmitting">{{ isSubmitting ? 'Enviando...' : 'Enviar' }}</button>
  </form>
</template>

<script setup>
import Swal from 'sweetalert2';
import { ref } from 'vue';
import { enviarRegistro } from '../services/api';

const ALLOWED_IMAGE_TYPES = ['image/png', 'image/jpeg', 'image/webp'];
const TEXT_ONLY_REGEX = /^[A-Za-zÁ-ÿ\s]+$/;
const NUMBERS_ONLY_REGEX = /^[0-9]+$/;
const EMAIL_REGEX = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;

const form = ref({
  nombres: '',
  apellidos: '',
  rol: '',
  rol_especificado: '',
  cedula: '',
  email: '',
  celular: ''
});

const files = ref({
  formato: []
});

const previews = ref({
  formato: []
});

const isSubmitting = ref(false);

const handleFile = (event) => {
  const selectedFiles = Array.from(event.target.files);

  if (selectedFiles.length !== 1) {
    Swal.fire('Formato', 'Debes seleccionar exactamente 1 imagen.', 'warning');
    return;
  }

  const file = selectedFiles[0];
  if (!ALLOWED_IMAGE_TYPES.includes(file.type)) {
    Swal.fire('Formato inválido', 'La imagen debe ser PNG, JPG o WEBP.', 'warning');
    return;
  }

  previews.value.formato.forEach(url => URL.revokeObjectURL(url));
  files.value.formato = [file];
  previews.value.formato = [URL.createObjectURL(file)];
};

const removeImage = (index) => {
  files.value.formato.splice(index, 1);
  URL.revokeObjectURL(previews.value.formato[index]);
  previews.value.formato.splice(index, 1);
};

const cleanDataBeforeSend = () => {
  const dataToSend = { ...form.value };
  if (dataToSend.rol !== 'otro') delete dataToSend.rol_especificado;
  return dataToSend;
};

const onlyText = v => TEXT_ONLY_REGEX.test(v || '');
const onlyNumbers = v => NUMBERS_ONLY_REGEX.test(v || '');
const validEmail = v => EMAIL_REGEX.test(v || '');
const validSingleImage = arr =>
  arr.length === 1 && ALLOWED_IMAGE_TYPES.includes(arr[0]?.type);

const validateForm = () => {
  const f = form.value;

  if (!onlyText(f.nombres)) return 'El campo "Nombres" solo permite letras y espacios.';
  if (!onlyText(f.apellidos)) return 'El campo "Apellidos" solo permite letras y espacios.';
  if (!f.rol) return 'Debes seleccionar una opción en "Rol".';
  if (!onlyNumbers(f.cedula)) return 'El campo "Cédula" solo permite números sin puntos ni espacios.';
  if (!validEmail(f.email)) return 'El campo "Email" debe contener un correo válido.';
  if (!onlyNumbers(f.celular)) return 'El campo "Celular" solo permite números.';
  if (!validSingleImage(files.value.formato))
    return 'Debes subir exactamente 1 imagen válida en "Formato de Compromiso".';

  return null;
};

const handleSubmit = async () => {
  if (isSubmitting.value) return;
  isSubmitting.value = true;

  const error = validateForm();
  if (error) {
    isSubmitting.value = false;
    Swal.fire('Error de validación', error, 'error');
    return;
  }

  const confirm = await Swal.fire({
    title: '¿Confirmar envío?',
    text: 'Estás a punto de registrar esta información. ¿Deseas continuar?',
    icon: 'question',
    showCancelButton: true,
    confirmButtonText: 'Sí, enviar',
    cancelButtonText: 'Cancelar'
  });

  if (!confirm.isConfirmed) {
    isSubmitting.value = false;
    return;
  }

  const formData = new FormData();
  const cleanData = cleanDataBeforeSend();
  formData.append('data', JSON.stringify(cleanData));

  if (files.value.formato[0]) {
    formData.append('formato', files.value.formato[0]);
  }

  Swal.fire({
    title: 'Enviando...',
    text: 'Estamos procesando tu registro',
    allowOutsideClick: false,
    allowEscapeKey: false,
    didOpen: () => Swal.showLoading()
  });

  try {
    await enviarRegistro(formData);
    Swal.fire('¡Éxito!', 'Tu registro ha sido enviado.', 'success');

    form.value = {
      nombres: '',
      apellidos: '',
      rol: '',
      rol_especificado: '',
      cedula: '',
      email: '',
      celular: ''
    };
    files.value = { formato: [] };
    previews.value = { formato: [] };
  } catch (err) {
    Swal.fire('Error', err.response?.data?.message || 'Error al enviar el formulario.', 'error');
  } finally {
    isSubmitting.value = false;
  }
};
</script>

<style scoped>
form {
  background-color: #f8f9fa; /* gris claro */
  padding: 2rem;
  border-radius: 8px;
  box-shadow: 0 4px 12px rgba(0, 0, 0, 0.05);
  font-family: 'ColgateReady-Regular';
}

label {
  font-weight: 500;
  color: #333;
}

input.form-control,
select.form-select {
  border-radius: 6px;
  border: 1px solid #ccc;
  padding: 0.6rem 0.75rem;
  font-size: 0.95rem;
}

input.form-control:focus,
select.form-select:focus {
  border-color: #6c757d;
  box-shadow: none;
}

h5 {
  margin-top: 2rem;
  font-size: 1.1rem;
  color: #444;
}

.alert-info {
  background-color: #e9f5ff;
  color: #31708f;
  border-color: #bce8f1;
  border-radius: 6px;
  padding: 1rem;
}

button.btn-primary {
  background-color: #007bff;
  border-color: #007bff;
  padding: 0.75rem;
  font-size: 1rem;
  border-radius: 6px;
}

button.btn-primary:disabled {
  background-color: #7aa7e9;
  border-color: #7aa7e9;
}

img.img-thumbnail {
  border-radius: 6px;
}

small.text-muted {
  font-size: 0.85rem;
}
</style>
